import Tooltip from '@mui/material/Tooltip';
import {IconButton} from '@mui/material';
import React, {Fragment, useEffect, useState} from 'react';
import makeStyles from '@mui/styles/makeStyles';
import moment from 'moment/moment';
import _ from 'lodash';
import {hasPermission, Permissions, sendUserAudit} from '../../../../store/reducers/user';
import {ASSESSMENT_MANDATORY_CONFIG, PATIENT_CONTEXT_TYPE} from '../../../../util/patientContextUtil';
import ContactMailIcon from '@mui/icons-material/ContactMail';
import SummarizeIcon from '@mui/icons-material/Summarize';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import {AgyleECDSIcon} from '../../../../components/icons/agyle';
import EmailIcon from '@mui/icons-material/Email';
import EjectIcon from '@mui/icons-material/Eject';
import CancelIcon from '@mui/icons-material/Cancel';
import {getSafeguardingValues, isClinicalConclusionNeeded, SEPSIS_TOOL_TYPE} from '../../forms/formsUtil';
import {
    ctxBlockDischargeMandatoryAssessment,
    ctxPatientBed,
    ctxPatientType,
    openPatientForm,
    setClinicalConclusionNeeded,
    setRemoveOption,
    setShowMandatoryAssessment,
} from '../../../../store/reducers/patientContext';
import {getPatientFlagValue} from '../util/patientFlagsUtil';
import {setPatientToTransfer} from '../../../../store/reducers/wardMap';
import {useDispatch, useSelector} from 'react-redux';
import {additionalData} from '../../../../store/reducers/additionalData';
import {useTranslation} from 'react-i18next';
import {getConfig} from '../../../../util/configUtil';
import {currentMap} from '../../../../store/reducers/ward';
import {setSnackbar} from '../../../../store/reducers/globals';
import {useParams} from 'react-router-dom';
import API from '../../../../app/api';
import MainScreenWarningDialog from './mainScreenWarningDialog';
import useOpenState from '../../../../hooks/useOpenState';
import {SepsisPathwayStatus} from '../../sepsis/sepsisUtil';
import IceDemoMock from '../../../../components/mock/iceDemoMock';
import EmptyDiagnosisDialog from '../../../utils/emptyDiagnosisDialog';
import {getAdmissionStartDate} from '../../../../util/patientUtil';
import MainScreenDocumentsButton from './buttons/mainScreenDocumentsButton';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import {nextWorkflowStep, workflow} from '../../../../store/reducers/workflow';
import {WORKFLOW_OPTIONS} from '../../../../util/workflowUtil';
import MainScreenDischargeButton from './buttons/mainScreenDischargeButton';
import {assessmentsConfig} from '../../../../store/reducers/externalConfig';
import {DEFAULT_ASSESSMENTS_VALUE} from '../../../admin/aglyeConfig/configDefaultValues';
import useAllAssessmentsFiled from '../../../../hooks/useAllAssessmentsFiled';
import {AUDIT_TYPES} from '../../../../util/auditUtil';

const useStyles = makeStyles((theme) => {
    return {
        dischargeButton: {
            '&:hover': {
                color: 'white',
            },
        },
        topActionButton: {
            backgroundColor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.mode === 'light' ? theme.palette.primary.main : '#313233'}`,
            color: theme.palette.mode === 'light' ? 'grey' : 'white',
            borderRadius: 5,
            height: 35,
        },
    };
});

const MainScreenMenuButtons = ({patient, disableSave, onSave, pastAttendances}) => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const {t} = useTranslation();

    // Selectors
    const patientAddData = useSelector(additionalData);
    const newCurrentMap = useSelector(currentMap);
    const bedObj = useSelector(ctxPatientBed);
    const patientType = useSelector(ctxPatientType);
    const workflowData = useSelector(workflow);
    const assCfg = useSelector(assessmentsConfig);
    const blockDischargeMandatoryAssessment = useSelector(ctxBlockDischargeMandatoryAssessment);

    const [mainScreenButtons, setMainScreenButtons] = useState([]);
    const [assessmentMandatory, setAssessmentMandatory] = useState(true);
    const [showMandatoryModal, setShowMandatoryModal] = useState(false);
    const {mapId} = useParams();

    // Menus state
    const [menuEl, setMenuEl] = useState(null);
    const open = Boolean(menuEl);

    // Configs
    const uhdMaps = getConfig('modules.map.customMap', false);
    const mandatorySafeguardingPaeds = getConfig('safeguardingDischargeMandatoryPaed', false);
    const mandatorySafeguardingAdult = getConfig('safeguardingDischargeMandatoryAdult', false);
    const enableIceDesktopButton = getConfig('enableIceDesktop', false);
    const enabledMockIceDesktopButton = getConfig('modules.demoProperties.displayMockIceButton', false);
    const enabledVODAgyleMiniFormButton = getConfig('verificationOfDeathAgyleMiniForm', false);
    const bedRequestModuleEnabled = getConfig('modules.bedManagement.bedRequest', false);
    const diagnosisRequiredDialog = getConfig('diagnosisRequiredDialog', false);
    const sepsisCompletedOnDischarge = getConfig('sepsisCompletedOnDischarge', false);

    // Warning dialog
    const [warningType, setWarningType] = useState(null);
    const [isWarningOpen, openWarning, closeWarning] = useOpenState(false, true);

    // Other open states
    const [isMockIceOpen, openMockIce, closeMockIce] = useOpenState(false, true);
    const [isEmptyDiagnosisDialogOpen, openEmptyDiagnosisDialog, closeEmptyDiagnosisDialog] = useOpenState(false, true);

    // Hooks
    useAllAssessmentsFiled(patient);

    useEffect(() => {
        const mandatory = assCfg?.mandatory || DEFAULT_ASSESSMENTS_VALUE.mandatory;
        setAssessmentMandatory(mandatory?.enabled && mandatory.showBorderline === ASSESSMENT_MANDATORY_CONFIG.SHOW_BORDERLINE_ON_DISCHARGE);
        setShowMandatoryModal(mandatory?.enabled && mandatory.modalDisplay === ASSESSMENT_MANDATORY_CONFIG.DISPLAY_MODAL_ON_DISCHARGE);
    }, [assCfg]);

    useEffect(() => {
        if (patientType === PATIENT_CONTEXT_TYPE.REVIEW) return null;

        const buttons = [];
        const isSeeAndTreatDraft = patient?.studies?.seeAndTreat?.status === 'draft';
        const patientIsDischarged = !!patient?.admission?.endDate && (patient?.admission?.status === 'DISCHARGE_REQUESTED' || patient?.admission?.status === 'TRANSFER_REQUESTED');
        const moveDischargeButton = patient?.admission?.status === 'DISCHARGE_REQUESTED' && uhdMaps;
        const isTimestamp = ptHaveTimestamp();

        if (dispatch(hasPermission(Permissions.AGYLE_CAN_CONFIRM_DEMOGRAPHICS)) && patientType === PATIENT_CONTEXT_TYPE.FAST_REGISTRY) {
            buttons.push({
                name: 'confirmDemographics',
                display: t('Confirm Demographics'),
                icon: <ContactMailIcon/>,
                width: 'auto',
                event: () => handleOpenWarning('demographic'),
            });
        }

        if (enableIceDesktopButton && patientType !== PATIENT_CONTEXT_TYPE.TEMPORARY) {
            buttons.push({
                name: 'ice',
                display: t('Ice Desktop'),
                disabled: disableSave,
                width: 'auto',
                icon: <SummarizeIcon/>,
                event: handleOpenUIceDesktop,
            });
        }

        if (enabledMockIceDesktopButton) {
            buttons.push({
                name: 'iceMock',
                display: t('Ice'),
                disabled: disableSave,
                width: 'auto',
                icon: <InsertDriveFileIcon/>,
                event: openMockIce,
            });
        }

        if ((dispatch(hasPermission(Permissions.AGYLE_IS_ECDS_ADMIN)) || dispatch(hasPermission(Permissions.AGYLE_CAN_VIEW_ECDS))) && patientType !== PATIENT_CONTEXT_TYPE.EDAU) {
            buttons.push({
                name: 'ecds',
                display: t('ECDS'),
                icon: <AgyleECDSIcon/>,
                width: 'auto',
                event: () => dispatch(openPatientForm('ecds')),
            });
        }

        if (patientType !== PATIENT_CONTEXT_TYPE.TEMPORARY || dispatch(hasPermission(Permissions.CAN_ACCESS_ADMIN_PAGE))) {
            buttons.push({
                name: 'documents',
                display: t('Documents'),
                icon: <PictureAsPdfIcon/>,
                buttonType: 'dropDown',
            });
            buttons.push({
                name: 'casMail',
                display: t('Mail'),
                icon: <EmailIcon/>,
                width: 'auto',
                event: () => dispatch(openPatientForm('ecdsEmail')),
            });
        }

        if (dispatch(hasPermission(Permissions.AGYLE_CAN_DISCHARGE_TEMPORARY)) && patientType === PATIENT_CONTEXT_TYPE.TEMPORARY) {
            buttons.push({
                name: 'dischargeRemove',
                display: t('Remove'),
                icon: <EjectIcon/>,
                color: 'primary',
                width: 'auto',
                isReadyToTransfer: true,
                buttonVariant: 'contained',
                event: () => {
                    dispatch(setRemoveOption('remove'));
                },
            });
        }

        if (dispatch(hasPermission(Permissions.AGYLE_CAN_DISCHARGE)) && patientType !== PATIENT_CONTEXT_TYPE.EDAU
            && moveDischargeButton && !patientIsDischarged) {
            buttons.push({
                name: 'dischargeMove',
                display: t('Move'),
                icon: <EjectIcon/>,
                color: 'primary',
                width: 'auto',
                buttonVariant: 'contained',
                event: () => {
                    dispatch(setRemoveOption('move'));
                },
            });
        }

        if (dispatch(hasPermission(Permissions.AGYLE_CAN_DISCHARGE))
            && (patientType !== PATIENT_CONTEXT_TYPE.EDAU && patientType !== PATIENT_CONTEXT_TYPE.TEMPORARY)
            && (!moveDischargeButton || patientIsDischarged)) {
            const isReadyToTransfer = bedRequestModuleEnabled && patient?.clinicalConclusion?.status === 'Admitted' && !!mapId;
            const text = isReadyToTransfer ? 'Admit to ward' : 'Discharge';
            buttons.push({
                name: 'discharge',
                display: t(text),
                icon: <CancelIcon/>,
                buttonVariant: 'contained',
                isReadyToTransfer: isReadyToTransfer,
                color: 'primary',
                event: (dischargeTemplate) => {
                    const diagNotReq = dischargeTemplate?.diagnosisRequired || false;
                    if (assessmentMandatory && !pastAttendances) dispatch(setShowMandatoryAssessment(true));

                    if (isTimestamp) {
                        handleOpenWarning('timestamp');
                    } else if (patientIsDischarged) {
                        dispatch(openPatientForm('discharge'));
                    } else if (isSeeAndTreatDraft) {
                        handleOpenWarning('seeAndTreatDraft');
                    } else if (!diagNotReq && diagnosisRequiredDialog && _.isEmpty(patient?.diagnosis?.primary) && !patient?.metadata?.diagnosisNotRequired?.acknowledge) {
                        openEmptyDiagnosisDialog();
                    } else if (checkSafeguarding(dischargeTemplate)) {
                        handleOpenWarning('safeguarding');
                    } else if (dispatch(hasPermission(Permissions.AGYLE_CAN_MANAGE_CLERKING)) && isClinicalConclusionNeeded(patient)) {
                        dispatch(setClinicalConclusionNeeded({
                            open: true,
                            fromDischarge: true,
                        }));
                    } else if (checkSepsis()) {
                        handleOpenWarning('sepsis');
                    } else if (enabledVODAgyleMiniFormButton && getPatientFlagValue(patient, 'deceased') && _.isNil(patient?.studies?.verificationOfDeath?.lastUpdateBy)) {
                        handleOpenWarning('vod');
                    }  else if (blockDischargeMandatoryAssessment && !pastAttendances) {
                        if (showMandatoryModal) {
                            handleOpenWarning('mandatoryAssessment');
                        } else {
                            dispatch(setSnackbar({
                                type: 'warning',
                                message: 'Please complete mandatory assessments before discharge.',
                            }));
                        }
                    } else if (isReadyToTransfer) {
                        dispatch(setPatientToTransfer({patient: patient, bed: bedObj}));
                    } else {
                        dispatch(openPatientForm('discharge'));
                    }
                },
            });
        }

        if (dispatch(hasPermission(Permissions.AGYLE_CAN_ONLY_DISCHARGE_EDAU)) && patientType === PATIENT_CONTEXT_TYPE.EDAU) {
            buttons.push({
                name: 'edauDischarge',
                display: t('EDAU Departure Checklist'),
                icon: <CancelIcon/>,
                color: 'primary',
                buttonVariant: 'contained',
                event: () => dispatch(openPatientForm('edauDischarge')),
            });
        }
        setMainScreenButtons(buttons);
    }, [
        patient,
        patientAddData,
        patientType,
        assessmentMandatory,
        blockDischargeMandatoryAssessment,
        pastAttendances,
    ]);

    useEffect(() => {
        if (workflowData.type === WORKFLOW_OPTIONS.OPEN_EMPTY_DIAGNOSIS_MODAL) {
            openEmptyDiagnosisDialog();
        }
    }, [workflowData]);

    function ptHaveTimestamp() {
        const clerking = patient?.clerking?.status === 'timestamp';
        const triage = patient?.triage && !patient?.triage?.discriminator && patient?.triage?.triageClinicians;
        const rapid = patient?.seen?.status === 'timestamp';
        const seeAndTreat = patient?.studies?.seeAndTreat?.status === 'timestamp';
        return clerking || triage || rapid || seeAndTreat;
    }

    const checkSepsis = () => {
        if (sepsisCompletedOnDischarge) {
            const pathway = _.filter(patientAddData?.other?.sepsisPathways, sepsis => sepsis?.statusObj === SepsisPathwayStatus.INCOMPLETE);
            const tools = _.filter(patientAddData?.other?.sepsisTool, tool => tool?.status === SEPSIS_TOOL_TYPE.IN_COMPLETED);
            return pathway?.length > 0 || tools?.length > 0;
        }
        return false;
    };

    const handleOpenMenu = (event, opts) => {
        setMenuEl(event.currentTarget);
    };

    const handleCloseMenu = () => {
        setMenuEl(null);
    };

    const checkSafeguarding = (template) => {
        const safeguardingNotRequired = template?.safeguardingNotRequired || false;
        let isSafeguardingWarning = false;
        const age = moment(getAdmissionStartDate(patient?.admission)).diff(patient?.patient?.dob, 'years');

        if ((mandatorySafeguardingPaeds && age < 18) ||
            (mandatorySafeguardingAdult && age >= 18)) {
            isSafeguardingWarning = true;
        }

        if (isSafeguardingWarning) {
            const mainSafeguarding = _.isEmpty(getSafeguardingValues(patient));
            return mainSafeguarding && !safeguardingNotRequired && _.isEmpty(template?.dischargeSafeguarding ?? []);
        }
        return false;
    }

    const handleOpenWarning = (type) => {
        openWarning();
        setWarningType(type);
    };

    const handleCloseWarning = () => {
        closeWarning();
        setWarningType(null);
    };

    const handleOpenUIceDesktop = () => {
        const org = newCurrentMap?.mapPath === 'poole' ? 'PCAS' : 'BED';
        dispatch(sendUserAudit(patient, AUDIT_TYPES.OPEN_ICE, {organization: org}));

        API.getICEServiceResponse(patient.patient?.__id, 'CUSTOM_AGL_OrderTest', org)
            .then(response => {
                if (response?.url) {
                    window.open(response?.url, '_blank').focus();
                } else {
                    dispatch(setSnackbar({type: 'error', message: 'Failed to open ICE Desktop.'}));
                }
            })
            .catch(e => {
                console.warn(e);
                dispatch(setSnackbar({type: 'error', message: 'Failed to open ICE Desktop.'}));
            });
    };

    const handleCloseEmptyDiag = (_e, reason) => {
        if (reason !== 'backdropClick') {
            closeEmptyDiagnosisDialog();
            if (workflowData.type === WORKFLOW_OPTIONS.OPEN_EMPTY_DIAGNOSIS_MODAL) {
                dispatch(nextWorkflowStep());
            }
        }
    };

    return (
        <>
            <div style={{display: 'flex', gap: 7, alignItems: 'center'}}>
                {mainScreenButtons.map(button => {
                    const isDischarge = button.name.toLowerCase().includes('discharge');
                    if (isDischarge) return <Fragment key="discharge-button"><MainScreenDischargeButton button={button}/></Fragment>;

                    return (
                        <Tooltip title={button.display} key={button.name}>
                            <span>
                                <IconButton
                                    className={classes.topActionButton}
                                    key={button.name}
                                    size="small"
                                    disabled={button.disabled || false}
                                    color={button.color || 'tertiary'}
                                    onClick={e => button.buttonType === 'dropDown'
                                        ? handleOpenMenu(e, button.options)
                                        : button.event()
                                    }
                                >
                                    {button.icon}
                                </IconButton>
                            </span>
                        </Tooltip>
                    );
                })}
            </div>

            <MainScreenWarningDialog
                open={isWarningOpen}
                onClose={handleCloseWarning}
                patient={patient}
                type={warningType}
            />

            <EmptyDiagnosisDialog
                open={isEmptyDiagnosisDialogOpen}
                onClose={handleCloseEmptyDiag}
                onSave={onSave}
                patient={patient}
            />

            <IceDemoMock
                open={isMockIceOpen}
                onClose={closeMockIce}
            />

            <MainScreenDocumentsButton
                open={open}
                onClose={handleCloseMenu}
                patient={patient}
                anchorEl={menuEl}
            />
        </>
    );
};

export default MainScreenMenuButtons;
